import React, { useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { JdsButton } from "../../components/jds/JdsButton";
import { JdsTag } from "../../components/jds/JdsTag";
import UserDetailsConfirmModal from "./UserDetailsConfirmModal";
import "../../components/jds/jds.css";
import "./userDetails.css";
import {
  getUser,
  inactivateUser,
  promoteUserToAdmin,
  demoteUserFromAdmin,
} from "../../api";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { getUserRole } from "../../helpers";
import { useLogoutAllDevices } from "../../hooks/useLogoutAllDevices";
import { useImpersonate } from "../../hooks/useImpersonate";
import { IMPERSONATE_TEXTS } from "../../constants/impersonate";
import { StatusCodes } from "http-status-codes";
import useFeatureFlag from "../../hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "../../constants/FeatureFlag";

const MODAL_TYPES = {
  INACTIVATE: "inactivate",
  PROMOTE: "promote",
  DEMOTE: "demote",
};

const DetailRow = ({ label, children }) => (
  <div className="jds-detail-row">
    <span className="text-muted">{label}</span>
    <span className="user-details__value">{children}</span>
  </div>
);

const UserDetails = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [selectedModalType, setSelectedModalType] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [user, setUser] = useState(null);

  const loggedAdminId = useSelector((state) => state.auth.user?.id);

  const { isFeatureFlagEnable: isAdminRoleFlagEnabled } = useFeatureFlag(
    FEATURE_FLAGS.PERMISSION.ADMIN_ROLE_ASSIGNMENT
  );

  const isAdminRoleAssignmentEnabled =
    isAdminRoleFlagEnabled ||
    process.env.REACT_APP_FORCE_ADMIN_ROLE_ASSIGNMENT === "true";

  const {
    showModal: showLogoutModal,
    setShowModal: setShowLogoutModal,
    openModal,
    closeModal,
    loading: logoutLoading,
    error: logoutError,
    handleLogout: handleLogoutAllDevices,
  } = useLogoutAllDevices(id);

  const { impersonate, isLoading: impersonateLoading } = useImpersonate();

  const isInactivable = user?.status === "active" && user?.role !== "client" && user?.role !== "admin";

  const isPromotable =
    isAdminRoleAssignmentEnabled &&
    user?.role === "client" &&
    user?.status === "active";

  const isDemotable =
    isAdminRoleAssignmentEnabled &&
    user?.role === "admin" &&
    user?.status === "active" &&
    user.id !== loggedAdminId;

  const clientId = user?.client_id || user?.parent_client_id;

  const closeSelectedModal = () => setSelectedModalType(null);

  const formatIsoToDateBr = (isoString) => {
    if (!isoString) return "";

    try {
      return format(parseISO(isoString), "dd/MM/yyyy");
    } catch (error) {
      toast.error("Data inválida");
      return "";
    }
  };

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await getUser(id);
      setUser(response.data);
    } catch (error) {
      toast.error("Erro ao buscar usuário.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleInactivateUser = async () => {
    try {
      await inactivateUser(id);
    } catch (error) {
      toast.error("Erro ao inativar o usuário.");
      console.error({error});
    } finally {
      fetchUser();
      closeSelectedModal();
    }
  };

  const handlePromoteUserToAdmin = async () => {
    setIsModalLoading(true);
    try {
      await promoteUserToAdmin(id);
      toast.success("Usuário promovido a administrador com sucesso!");
      closeSelectedModal();
      await fetchUser();
    } catch (error) {
      const status = error?.response?.status;

      switch (status) {
        case StatusCodes.UNAUTHORIZED:
          toast.error("Você não tem permissão para realizar esta ação.");
          break;
        case StatusCodes.FORBIDDEN:
          toast.error("Você não tem permissões suficientes ou o usuário não é do role Principal.");
          break;
        case StatusCodes.NOT_FOUND:
          toast.error("Funcionalidade indisponível ou usuário não encontrado.");
          break;
        case StatusCodes.CONFLICT:
          toast.error("Usuário já é administrador.");
          break;
        default:
          toast.error("Não foi possível promover o usuário. Tente novamente.");
      }

      closeSelectedModal();
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleDemoteUserFromAdmin = async () => {
    setIsModalLoading(true);
    try {
      await demoteUserFromAdmin(id);
      toast.success("Administrador despromovido com sucesso!");
      closeSelectedModal();
      await fetchUser();
    } catch (error) {
      const status = error?.response?.status;

      switch (status) {
        case StatusCodes.UNAUTHORIZED:
          toast.error("Você não tem permissão para realizar esta ação.");
          break;
        case StatusCodes.FORBIDDEN:
          toast.error("Não é permitido despromover a si mesmo.");
          break;
        case StatusCodes.NOT_FOUND:
          toast.error("Funcionalidade indisponível ou usuário não encontrado.");
          break;
        case StatusCodes.CONFLICT:
          toast.error("Este usuário não é administrador.");
          break;
        default:
          toast.error("Não foi possível despromover o usuário. Tente novamente.");
      }

      closeSelectedModal();
    } finally {
      setIsModalLoading(false);
    }
  };

  const onNavigateToClient = () => {
    if (clientId) {
      window.open(`/clientes/${clientId}`, "_blank");
    }
  };

  const handleImpersonateClick = () => {
    impersonate({ userId: id });
  };

  const renderUserInfo = () => {
    if (!user) return null;

    return (
      <>
        <div className="user-details__rows">
          <DetailRow label="ID">{user.id}</DetailRow>
          <DetailRow label="Nome">{user.name}</DetailRow>
          <DetailRow label="Função">{getUserRole(user.role)}</DetailRow>
          <DetailRow label="E-mail">{user.email}</DetailRow>
          <DetailRow label="Status de verificação">
            <JdsTag
              label={user.verified ? "Verificado" : "Não verificado"}
              color={user.verified ? "success" : "neutral"}
            />
          </DetailRow>
          <DetailRow label="Data de criação">{formatIsoToDateBr(user.created_at)}</DetailRow>
          <DetailRow label="Última modificação">{formatIsoToDateBr(user.modified_at)}</DetailRow>
          <DetailRow label="Fonte de registro">{user.registration_source}</DetailRow>
          <DetailRow label="Cidade de referência">
            {user.reference_city ? user.reference_city : "Não informado"}
          </DetailRow>
          <DetailRow label="Permissão para usar JusDecision">
            <JdsTag
              label={user.may_use_jusdecision ? "Permitido" : "Não permitido"}
              color={user.may_use_jusdecision ? "success" : "neutral"}
            />
          </DetailRow>
        </div>

        <div className="user-details__actions">
          <JdsButton onClick={handleImpersonateClick} disabled={impersonateLoading}>
            {IMPERSONATE_TEXTS.BUTTON_LABEL}
          </JdsButton>

          {clientId ? (
            <JdsButton variant="outlined" color="neutral" onClick={onNavigateToClient}>
              Acessar cliente
            </JdsButton>
          ) : null}

          {isPromotable && (
            <JdsButton variant="outlined" onClick={() => setSelectedModalType(MODAL_TYPES.PROMOTE)}>
              Promover a administrador
            </JdsButton>
          )}

          <JdsButton variant="outlined" color="neutral" onClick={openModal}>
            Deslogar de todos os dispositivos
          </JdsButton>

          {isInactivable && (
            <JdsButton
              variant="outlined"
              color="error"
              onClick={() => setSelectedModalType(MODAL_TYPES.INACTIVATE)}
            >
              Inativar usuário
            </JdsButton>
          )}

          {isDemotable && (
            <JdsButton
              variant="outlined"
              color="error"
              onClick={() => setSelectedModalType(MODAL_TYPES.DEMOTE)}
            >
              Despromover administrador
            </JdsButton>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="jds-scope">
      <BlockUi tag="div" blocking={loading}>
        {user && (
          <div className="jds-card user-details">
            <h1 className="jds-title-md user-details__header">Detalhes do usuário</h1>
            {renderUserInfo()}
          </div>
        )}

        {selectedModalType === MODAL_TYPES.INACTIVATE && (
          <UserDetailsConfirmModal
            message="Tem certeza que deseja inativar este usuário?"
            onConfirm={handleInactivateUser}
            onClose={closeSelectedModal}
            confirmColor="error"
          />
        )}

        {selectedModalType === MODAL_TYPES.PROMOTE && (
          <UserDetailsConfirmModal
            message="Tem certeza que deseja promover este usuário a administrador?"
            onConfirm={handlePromoteUserToAdmin}
            onClose={closeSelectedModal}
            isLoading={isModalLoading}
          />
        )}

        {selectedModalType === MODAL_TYPES.DEMOTE && (
          <UserDetailsConfirmModal
            message="Tem certeza que deseja despromover este administrador? Ele perderá o acesso ao painel admin."
            onConfirm={handleDemoteUserFromAdmin}
            onClose={closeSelectedModal}
            isLoading={isModalLoading}
            confirmColor="error"
          />
        )}

        {showLogoutModal && (
          <UserDetailsConfirmModal
            message="Tem certeza que deseja deslogar este usuário de todos os dispositivos?"
            onConfirm={handleLogoutAllDevices}
            onClose={closeModal}
            isLoading={logoutLoading}
            confirmLabel={logoutLoading ? "Processando..." : "Confirmar"}
            confirmColor="error"
            error={logoutError}
          />
        )}
      </BlockUi>
    </div>
  );
};

export default UserDetails;
